import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Newspaper } from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { getSortedReleases } from '../../data/pressReleases';

const SITE = 'https://staymii.com';

// Newest first — single source of truth in data/pressReleases.js.
const SORTED_RELEASES = getSortedReleases();

const collectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',  name: 'StayMii Press & News',
  url: SITE + '/press',
  description: 'Press releases and news from StayMii — Malaysia\u2019s hospitality and tourism technology platform.',
  hasPart: SORTED_RELEASES.map((r) => ({
    '@type': 'NewsArticle',
    headline: r.title,
    datePublished: r.dateIso,
    url: SITE + r.slug,
    image: r.image ? [r.image] : undefined,
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Press', item: SITE + '/press' },
  ],
};

export default function PressHub() {
  return (
    <div className="min-h-screen bg-white" data-testid="press-hub-page">
      <SEO
        title="Press & News — StayMii"
        description="Latest press releases and partnership announcements from StayMii, Malaysia's hospitality and tourism technology platform."
        keywords="StayMii press, StayMii news, IHCC StayMii, SGMC, Beds24 e-invoice Malaysia"
        canonicalUrl="/press"
      />
      <StructuredData id="press-hub" schema={[collectionSchema, breadcrumbSchema]} />
      <LandingNavBar />

      <section className="max-w-5xl mx-auto px-6 pt-28 pb-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-blue-700 font-semibold mb-3">
          <Newspaper className="w-4 h-4" /> Press &amp; News
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
          What&rsquo;s new at StayMii&trade;
        </h1>
        <p className="text-base text-slate-600 max-w-2xl">
          Partnership announcements, product launches and industry updates from Malaysia&rsquo;s hospitality and tourism technology platform.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-20">
        <ul className="divide-y divide-slate-200 border-t border-b border-slate-200" data-testid="press-releases-list">
          {SORTED_RELEASES.map((r, i) => (
            <li key={r.slug}>
              <Link
                to={r.slug}
                className="block py-8 hover:bg-slate-50 -mx-6 px-6 rounded-lg transition-colors"
                data-testid={`press-release-item-${i}`}
              >
                <div className="grid sm:grid-cols-[220px_1fr] gap-6 items-start">
                  {r.image ? (
                    <div className={`aspect-[4/3] rounded-lg overflow-hidden ${r.imageBg || 'bg-slate-100'}`}>
                      <img
                        src={r.image}
                        alt=""
                        loading="lazy"
                        className={`w-full h-full ${r.imageFit === 'contain' ? 'object-contain p-3' : 'object-cover'}`}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] rounded-lg bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center">
                      <Newspaper className="w-10 h-10 text-blue-300" />
                    </div>
                  )}
                  <div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-2">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.date}</span>
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wider">{r.tag}</span>
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2 hover:text-blue-700 transition-colors leading-snug">{r.title}</h2>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{r.dek}</p>
                    <span className="inline-flex items-center gap-1 text-sm text-blue-600 font-semibold">
                      Read the release <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-16 rounded-2xl border border-slate-200 bg-slate-50 p-8 sm:p-10">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Media enquiries</h3>
          <p className="text-sm text-slate-600 mb-4 max-w-2xl">
            For interview requests, press assets, or partnership announcements, reach the StayMii media team directly. We reply within one business day.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="mailto:media@staymii.com" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800" data-testid="press-media-email">media@staymii.com</a>
            <Link to="/sgmc" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-800 text-sm font-semibold hover:border-slate-400" data-testid="press-sgmc-link">
              About the SGMC Program <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
